import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { type AtticErrorFragment, useAtticIntakeMutation } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { useState } from "react";
import { useIntl } from "react-intl";

import AtticIntakePage from "../components/AtticIntakePage/AtticIntakePage";
import {
  type AtticIntakeFormData,
  emptyIntakeForm,
  formToIntakeInput,
  isIntakeValid,
} from "../intake";
import { intakeMessages } from "../messages";
import { atticLotUrl } from "../urls";

export const AtticIntakeView = () => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [data, setData] = useState<AtticIntakeFormData>(emptyIntakeForm);
  const [errors, setErrors] = useState<AtticErrorFragment[]>([]);
  const [saveState, setSaveState] = useState<ConfirmButtonTransitionState>("default");
  const [intake] = useAtticIntakeMutation({
    disableNestedErrorHandling: true,
    // Lots and stock pages read from the network next time they open.
    refetchQueries: ["AtticLotList", "AtticStockLookup", "AtticStockMovementList"],
    onCompleted: result => {
      const payload = result.atticIntake;
      const refused = payload?.errors ?? [];
      const lot = payload?.lot;

      setErrors(refused);

      if (!refused.length && lot) {
        setSaveState("success");
        notify({ status: "success", text: intl.formatMessage(intakeMessages.saved) });
        navigate(atticLotUrl(lot.id));
      } else {
        setSaveState("error");
      }
    },
    onError: () => setSaveState("error"),
  });

  return (
    <AtticIntakePage
      data={data}
      errors={errors}
      disabled={saveState === "loading"}
      saveButtonBarState={saveState}
      onChange={next => {
        setData(next);

        // A refusal described the lines as they were sent; once the user edits
        // the form it no longer lines up.
        if (errors.length) {
          setErrors([]);
        }
      }}
      onSubmit={() => {
        if (!isIntakeValid(data)) {
          return;
        }

        setErrors([]);
        setSaveState("loading");
        intake({ variables: { input: formToIntakeInput(data) } });
      }}
    />
  );
};

export default AtticIntakeView;
